/**
 * Outil « Mur des fractions » (Boîte à outils, 6e → 3e).
 *
 * Mur : deux fractions en barres au-dessus du mur des unités à douzièmes ;
 * toucher une case règle la fraction choisie, les fractions égales s'allument
 * sur le mur, la comparaison s'explique (même dénominateur, même numérateur,
 * dénominateur commun) ; « comme un quotient » : n/d = n ÷ d.
 * D'une quantité : barre partagée en d parts, on en prend n.
 * Somme / différence : des parts de même taille avant d'additionner (6e :
 * même dénominateur seulement). Dès la 4e : produit (aire d'un carré unité)
 * et division (combien de fois b dans a ; multiplier par l'inverse).
 * Calculs : `fractions.ts`.
 */

import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, Minus, Plus } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { NumberField, Note, Segmented } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS, INK, MUTED } from '../../ui/svg-parts';
import {
  addSteps,
  compareSteps,
  divSteps,
  exactDecimal,
  fitSentence,
  frAtoms,
  frPlain,
  frTex,
  frWords,
  lcm,
  mulSteps,
  ofQuantitySteps,
  partName,
  piecesOf,
  quotientSteps,
  simplify,
  wallEquivalents,
  WALL_MAX,
  type Fr,
  type Step,
} from './fractions';

type Mode = 'wall' | 'of' | 'add' | 'mul' | 'div';

const C1 = 'var(--color-primary-500)';
const C1_LIGHT = 'var(--color-primary-100)';
const C2 = 'var(--color-accent-500)';
const C2_LIGHT = 'var(--color-accent-100)';
const LINE = 'var(--color-ink-300)';

const VB_W = 340;
const X0 = 40;
const BAR_W = 292;

export function FractionWallTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = Math.min(4, Math.max(1, schoolLevelRank(level) ?? 3));
  const [mode, setMode] = useState<Mode>('wall');
  const options: Array<{ value: Mode; label: string }> = [
    { value: 'wall', label: 'Mur' },
    { value: 'of', label: 'Quantité' },
    { value: 'add', label: 'Somme' },
    ...(rank >= 3
      ? [
          { value: 'mul' as const, label: 'Produit' },
          { value: 'div' as const, label: 'Division' },
        ]
      : []),
  ];
  const send = { onSend, sendCost };
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Mode" value={mode} options={options} onChange={setMode} />
      {/* les modes restent montés : en changer ne perd pas les fractions réglées */}
      <div hidden={mode !== 'wall'} className="space-y-4">
        <WallMode rank={rank} />
      </div>
      <div hidden={mode !== 'of'} className="space-y-4">
        <OfMode {...send} />
      </div>
      <div hidden={mode !== 'add'} className="space-y-4">
        <AddMode rank={rank} {...send} />
      </div>
      {rank >= 3 && (
        <>
          <div hidden={mode !== 'mul'} className="space-y-4">
            <MulMode {...send} />
          </div>
          <div hidden={mode !== 'div'} className="space-y-4">
            <DivMode {...send} />
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* commandes                                                           */
/* ------------------------------------------------------------------ */

/** numérateur sur dénominateur, avec deux compteurs */
function FractionPicker({
  name,
  color,
  value,
  onChange,
  minNum = 0,
  maxNum = (d) => d,
  minDen = 1,
  maxDen = WALL_MAX,
  fixedDen = false,
  header,
}: {
  name: string;
  color: string;
  value: Fr;
  onChange: (f: Fr) => void;
  minNum?: number;
  maxNum?: (d: number) => number;
  minDen?: number;
  maxDen?: number;
  /** 6e : la 2e fraction garde le dénominateur de la 1re */
  fixedDen?: boolean;
  header?: ReactNode;
}) {
  return (
    <div className="border-ink-100 space-y-1 rounded-2xl border p-1.5">
      {header ?? (
        <p className="text-ink-700 flex items-center gap-2 px-1 text-sm font-semibold">
          <span className="h-3 w-3 rounded-full" style={{ background: color }} aria-hidden />
          {name}
        </p>
      )}
      <FracStepper
        label="numérateur"
        ariaName={`${name}, numérateur`}
        value={value.n}
        min={minNum}
        max={maxNum(value.d)}
        onChange={(n) => onChange({ ...value, n })}
      />
      <div className="bg-ink-700 mx-6 h-0.5 rounded" aria-hidden />
      {fixedDen ? (
        <p className="text-ink-500 py-2 text-center text-xs">
          dénominateur{' '}
          <span className="text-ink-900 font-display block text-lg font-bold">{value.d}</span>
        </p>
      ) : (
        <FracStepper
          label="dénominateur"
          ariaName={`${name}, dénominateur`}
          value={value.d}
          min={minDen}
          max={maxDen}
          onChange={(d) => onChange({ n: Math.min(Math.max(value.n, minNum), maxNum(d)), d })}
        />
      )}
    </div>
  );
}

/** compteur de fraction : l'étiquette AU-DESSUS (« dénominateur » ne tient pas entre deux boutons de 44 px) */
function FracStepper({
  label,
  ariaName,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  ariaName: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const btn =
    'text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-40';
  return (
    <div className="text-center">
      <span className="text-ink-500 block text-xs">{label}</span>
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={`${ariaName} : moins`}
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
          className={btn}
        >
          <Minus className="h-5 w-5" aria-hidden />
        </button>
        <span className="text-ink-900 font-display text-xl font-bold tabular-nums">{value}</span>
        <button
          type="button"
          aria-label={`${ariaName} : plus`}
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
          className={btn}
        >
          <Plus className="h-5 w-5" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** bouton qui déplie une explication et reste en place (le focus ne saute pas) */
function Disclosure({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <section className="border-ink-100 rounded-2xl border px-3 py-1">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="text-ink-800 flex min-h-11 w-full items-center justify-between gap-2 text-left text-sm font-semibold"
      >
        {label}
        <ChevronDown
          className={cn('h-5 w-5 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      <div id={id} hidden={!open} className="pb-2">
        {open && children}
      </div>
    </section>
  );
}

function StepList({ steps, testId }: { steps: Step[]; testId?: string }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      {steps.map((s, i) => (
        <div key={i} className="space-y-0.5">
          <MathText className="text-ink-900 block overflow-x-auto py-0.5 text-lg">{`$${s.tex}$`}</MathText>
          <p className="text-ink-600 text-sm">{frenchSpaces(s.say)}</p>
        </div>
      ))}
    </div>
  );
}

function SendResult({
  result,
  onSend,
  sendCost,
}: {
  result: Fr;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  if (!onSend) return null;
  const r = simplify(result);
  const dec = exactDecimal(r);
  return (
    <div className="flex flex-wrap gap-2">
      <SendToSheetButton
        label={frPlain(r)}
        cost={sendCost}
        onClick={() => onSend({ atoms: frAtoms(r) })}
      />
      {dec && r.d !== 1 && (
        <SendToSheetButton
          label={dec}
          cost={sendCost}
          onClick={() => onSend({ atoms: dec.startsWith('−') ? ['−', dec.slice(1)] : [dec] })}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Mur                                                                 */
/* ------------------------------------------------------------------ */

function WallMode({ rank }: { rank: number }) {
  const [f1, setF1] = useState<Fr>({ n: 2, d: 3 });
  // 6e-5e : un dénominateur multiple de l'autre ; dès la 4e, un multiple commun
  const [f2, setF2] = useState<Fr>(rank <= 2 ? { n: 5, d: 6 } : { n: 3, d: 4 });
  const [active, setActive] = useState<1 | 2>(1);
  const activeF = active === 1 ? f1 : f2;
  const cmp = compareSteps(f1, f2, rank);
  const eqs = wallEquivalents(activeF);
  const irreducible = simplify(activeF);

  const header = (k: 1 | 2) => (
    <button
      type="button"
      aria-pressed={active === k}
      onClick={() => setActive(k)}
      className={cn(
        'flex min-h-11 w-full items-center gap-2 rounded-xl px-2 text-left text-sm font-semibold',
        active === k ? 'bg-primary-50 text-primary-700' : 'text-ink-700 hover:bg-ink-50',
      )}
    >
      <span
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ background: k === 1 ? C1 : C2 }}
        aria-hidden
      />
      Fraction {k}
      {active === k && <span className="sr-only"> (le mur la règle)</span>}
    </button>
  );

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <FractionPicker
          name="Fraction 1"
          color={C1}
          value={f1}
          onChange={(f) => {
            setF1(f);
            setActive(1);
          }}
          header={header(1)}
        />
        <FractionPicker
          name="Fraction 2"
          color={C2}
          value={f2}
          onChange={(f) => {
            setF2(f);
            setActive(2);
          }}
          header={header(2)}
        />
      </div>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          `Touche une case du mur : elle règle la fraction ${active} (jusqu’à cette case).`,
        )}
      </p>
      <WallFigure
        f1={f1}
        f2={f2}
        active={active}
        onPick={(f) => (active === 1 ? setF1(f) : setF2(f))}
      />
      <div className="space-y-2" aria-live="polite">
        <div data-testid="wall-compare" aria-hidden>
          <MathText className="text-ink-900 block text-center text-2xl">
            {`$${frTex(f1)} ${cmp.rel} ${frTex(f2)}$`}
          </MathText>
        </div>
        <p className="sr-only">
          {frWords(f1)}{' '}
          {cmp.rel === '<'
            ? 'est plus petit que'
            : cmp.rel === '>'
              ? 'est plus grand que'
              : 'est égal à'}{' '}
          {frWords(f2)}
        </p>
      </div>
      <Disclosure label={frenchSpaces('Pourquoi ?')}>
        <StepList steps={cmp.steps} testId="wall-why" />
      </Disclosure>
      <Note live={false}>
        <span className="block" data-testid="wall-equivalents">
          {activeF.n === 0 ? (
            <>
              <MathText>{`$${frTex(activeF)} = 0$`}</MathText>
              {frenchSpaces(' : zéro part, quelle que soit leur taille.')}
            </>
          ) : eqs.length > 1 ? (
            <>
              {frenchSpaces('Égales sur le mur : ')}
              <MathText>{`$${eqs.map(frTex).join(' = ')}$`}</MathText>
            </>
          ) : (
            <>
              Sur le mur, aucune autre fraction n’est égale à{' '}
              <MathText>{`$${frTex(activeF)}$`}</MathText>.
            </>
          )}
        </span>
        {activeF.n > 0 && irreducible.d !== activeF.d && (
          <span className="mt-1 block">
            <MathText>{`$${frTex(activeF)}$`}</MathText> se simplifie par{' '}
            {activeF.d / irreducible.d}
            {frenchSpaces(' : forme irréductible ')}
            <MathText>{`$${frTex(irreducible)}$`}</MathText>.
          </span>
        )}
      </Note>
      {activeF.n > 0 && (
        <Disclosure label="Comme un quotient">
          <StepList steps={quotientSteps(activeF)} testId="wall-quotient" />
        </Disclosure>
      )}
    </>
  );
}

const BAR_H = 26;
const ROW_H = 24;
const ROW_GAP = 4;

function WallFigure({
  f1,
  f2,
  active,
  onPick,
}: {
  f1: Fr;
  f2: Fr;
  active: 1 | 2;
  onPick: (f: Fr) => void;
}) {
  const x = (v: number) => X0 + Math.min(1, v) * BAR_W;
  const bars = [
    { f: f1, y: 6, color: C1 },
    { f: f2, y: 6 + BAR_H + 8, color: C2 },
  ];
  const wallY = 6 + 2 * (BAR_H + 8) + 8;
  const H = wallY + WALL_MAX * (ROW_H + ROW_GAP) + 2;
  const activeF = active === 1 ? f1 : f2;
  const eq = new Map(wallEquivalents(activeF).map((e) => [e.d, e.n]));
  const light = active === 1 ? C1_LIGHT : C2_LIGHT;
  const accent = active === 1 ? C1 : C2;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Mur des fractions : ${frWords(f1)} en bleu, ${frWords(f2)} en jaune ; les fractions égales à ${frWords(activeF)} sont allumées.`}
    >
      {bars.map(({ f, y, color }, k) => (
        <g key={k}>
          <text
            x={X0 - 6}
            y={y + BAR_H / 2 + 5}
            textAnchor="end"
            fontSize={14}
            fontWeight={700}
            fill={INK}
          >
            {frPlain(f)}
          </text>
          {Array.from({ length: f.d }, (_, i) => (
            <rect
              key={i}
              x={X0 + (i * BAR_W) / f.d}
              y={y}
              width={BAR_W / f.d}
              height={BAR_H}
              fill={i < f.n ? color : 'transparent'}
              stroke={i < f.n ? color : LINE}
              strokeWidth={1.2}
            />
          ))}
          <rect
            x={X0}
            y={y}
            width={BAR_W}
            height={BAR_H}
            fill="none"
            stroke={INK}
            strokeWidth={1.2}
          />
        </g>
      ))}
      {Array.from({ length: WALL_MAX }, (_, r) => {
        const d = r + 1;
        const y = wallY + r * (ROW_H + ROW_GAP);
        const lit = eq.get(d) ?? -1;
        return (
          <g key={d}>
            <text x={X0 - 6} y={y + ROW_H / 2 + 4} textAnchor="end" fontSize={12} fill={MUTED}>
              {d === 1 ? '1' : `1/${d}`}
            </text>
            {Array.from({ length: d }, (_, i) => (
              <g key={i} className="cursor-pointer" onClick={() => onPick({ n: i + 1, d })}>
                <rect
                  data-testid={`wall-cell-${d}-${i + 1}`}
                  x={X0 + (i * BAR_W) / d}
                  y={y}
                  width={BAR_W / d}
                  height={ROW_H}
                  rx={2}
                  fill={i < lit ? light : 'transparent'}
                  stroke={i < lit ? accent : LINE}
                  strokeWidth={1}
                />
                {/* l'interligne compte aussi : la cible fait toute la hauteur de la ligne */}
                <rect
                  x={X0 + (i * BAR_W) / d}
                  y={y + ROW_H}
                  width={BAR_W / d}
                  height={ROW_GAP}
                  fill="transparent"
                />
              </g>
            ))}
          </g>
        );
      })}
      {bars.map(({ f, color }, k) => (
        <line
          key={k}
          x1={x(f.n / f.d)}
          x2={x(f.n / f.d)}
          y1={4}
          y2={H - 2}
          stroke={color}
          strokeWidth={2}
          strokeDasharray={k === 0 ? '6 4' : '2 4'}
          pointerEvents="none"
        />
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* D'une quantité                                                      */
/* ------------------------------------------------------------------ */

function OfMode({ onSend, sendCost }: ToolProps) {
  const [f, setF] = useState<Fr>({ n: 3, d: 4 });
  const [q, setQ] = useState(20);
  const r = ofQuantitySteps(f, q);
  return (
    <>
      <div className="grid grid-cols-2 items-start gap-2">
        <FractionPicker
          name="Fraction"
          color={C1}
          value={f}
          minNum={1}
          minDen={2}
          onChange={setF}
        />
        <NumberField label="Quantité" value={q} min={1} max={999} onChange={setQ} />
      </div>
      <TapeFigure f={f} q={q} part={r.part} result={r.result} />
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3">
        <StepList steps={r.steps} testId="of-steps" />
        <SendResult result={r.result} onSend={onSend} sendCost={sendCost} />
      </section>
    </>
  );
}

const shortValue = (f: Fr) => {
  const s = simplify(f);
  return exactDecimal(s) ?? frPlain(s);
};

function TapeFigure({ f, q, part, result }: { f: Fr; q: number; part: Fr; result: Fr }) {
  const W = BAR_W + 20;
  const x0 = 14;
  const y = 34;
  const h = 36;
  const pw = W / f.d;
  const label = shortValue(part);
  const fits = label.length * 7.5 + 6 <= pw;
  const H = fits ? 120 : 138;
  const xEnd = x0 + f.n * pw;
  const resultText = `${frPlain(f)} de ${q} = ${shortValue(result)}`;
  // centré sous ce qu'on prend, sans sortir de la figure
  const half = (resultText.length * 8) / 2;
  const resultX = Math.min(VB_W - 6 - half, Math.max(6 + half, (x0 + xEnd) / 2));
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`${q} partagé en ${f.d} parts égales de ${shortValue(part)} ; ${f.n} part${f.n > 1 ? 's' : ''} font ${shortValue(result)}.`}
    >
      {/* le tout */}
      <path
        d={`M ${x0} ${y - 6} L ${x0} ${y - 14} L ${x0 + W} ${y - 14} L ${x0 + W} ${y - 6}`}
        fill="none"
        stroke={MUTED}
        strokeWidth={1.5}
      />
      <text x={x0 + W / 2} y={y - 19} textAnchor="middle" fontSize={14} fontWeight={700} fill={INK}>
        {q}
      </text>
      {Array.from({ length: f.d }, (_, i) => (
        <g key={i}>
          <rect
            x={x0 + i * pw}
            y={y}
            width={pw}
            height={h}
            fill={i < f.n ? C1_LIGHT : 'transparent'}
            stroke={i < f.n ? C1 : LINE}
            strokeWidth={1.5}
          />
          {fits && (
            <text
              x={x0 + (i + 0.5) * pw}
              y={y + h / 2 + 5}
              textAnchor="middle"
              fontSize={13}
              fill={INK}
            >
              {label}
            </text>
          )}
        </g>
      ))}
      <rect x={x0} y={y} width={W} height={h} fill="none" stroke={INK} strokeWidth={1.5} />
      {/* ce qu'on prend */}
      <path
        d={`M ${x0} ${y + h + 6} L ${x0} ${y + h + 14} L ${xEnd} ${y + h + 14} L ${xEnd} ${y + h + 6}`}
        fill="none"
        stroke={C1}
        strokeWidth={2}
      />
      <text
        x={resultX}
        y={y + h + 32}
        textAnchor="middle"
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        {resultText}
      </text>
      {!fits && (
        <text x={x0} y={y + h + 52} fontSize={12} fill={MUTED}>
          {frenchSpaces(`1 part : ${label}`)}
        </text>
      )}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Somme / différence                                                  */
/* ------------------------------------------------------------------ */

function AddMode({ rank, onSend, sendCost }: ToolProps & { rank: number }) {
  // 6e : même dénominateur ; 5e : l'un multiple de l'autre ; dès la 4e : un multiple commun
  const [a, setA] = useState<Fr>(rank <= 1 ? { n: 3, d: 8 } : { n: 1, d: 2 });
  const [b, setB] = useState<Fr>(
    rank <= 1 ? { n: 2, d: 8 } : rank === 2 ? { n: 1, d: 4 } : { n: 1, d: 3 },
  );
  const [op, setOp] = useState<'+' | '-'>('+');
  const sameDen = rank <= 1;
  // 6e : même dénominateur, et on n'enlève jamais plus qu'on n'a (pas de relatifs)
  const bb = sameDen ? { n: Math.min(b.n, a.d, op === '-' ? a.n : a.d), d: a.d } : b;
  const negative = op === '-' && bb.n * a.d > a.n * bb.d;
  // les fractions négatives arrivent en 4e
  const hideSteps = negative && rank <= 2;
  const r = addSteps(a, bb, op);
  const D = lcm(a.d, bb.d);
  return (
    <>
      <Segmented
        ariaLabel="Opération"
        value={op}
        options={[
          { value: '+', label: 'Additionner' },
          { value: '-', label: 'Soustraire' },
        ]}
        onChange={setOp}
      />
      <div className="grid grid-cols-2 gap-2">
        <FractionPicker name="Fraction 1" color={C1} value={a} onChange={setA} />
        <FractionPicker
          name="Fraction 2"
          color={C2}
          value={bb}
          onChange={setB}
          fixedDen={sameDen}
          maxNum={(d) => (sameDen && op === '-' ? a.n : d)}
        />
      </div>
      {sameDen && (
        <p className="text-ink-500 text-xs">
          {frenchSpaces(
            'En 6e, on additionne des fractions de même dénominateur : la 2e prend celui de la 1re.',
          )}
        </p>
      )}
      {negative ? (
        <Note>
          <MathText>{`$${frTex(a)} < ${frTex(bb)}$`}</MathText>
          {frenchSpaces(
            rank <= 2
              ? ' : on ne peut pas enlever plus qu’on n’a (les fractions négatives arrivent en 4e).'
              : ' : on enlève plus qu’on n’a, la différence est négative.',
          )}
        </Note>
      ) : (
        <SumFigure a={a} b={bb} op={op} D={D} result={r.result} />
      )}
      {!sameDen && rank <= 2 && D !== Math.max(a.d, bb.d) && (
        <Note live={false}>
          {frenchSpaces(
            'Ici, aucun dénominateur n’est multiple de l’autre : chercher un multiple commun se fait en 4e.',
          )}
        </Note>
      )}
      {!hideSteps && (
        <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3">
          <StepList steps={r.steps} testId="add-steps" />
          <SendResult result={r.result} onSend={onSend} sendCost={sendCost} />
        </section>
      )}
    </>
  );
}

function SumFigure({
  a,
  b,
  op,
  D,
  result,
}: {
  a: Fr;
  b: Fr;
  op: '+' | '-';
  D: number;
  result: Fr;
}) {
  const hatch = useId().replace(/:/g, '');
  const units = op === '+' && a.n * b.d + b.n * a.d > a.d * b.d ? 2 : 1;
  // marge à droite : l'étiquette de la dernière graduation reste visible
  const unitW = (BAR_W - 10) / units;
  const x = (v: number) => X0 + v * unitW;
  const rowH = 24;
  const rows = [
    { f: a, y: 8, color: C1, light: C1_LIGHT, start: 0 },
    { f: b, y: 8 + rowH + 8, color: C2, light: C2_LIGHT, start: 0 },
  ];
  const yRes = 8 + 2 * (rowH + 8) + 10;
  const axisY = yRes + rowH + 14;
  const H = axisY + 22;
  const va = a.n / a.d;
  const vb = b.n / b.d;
  const cells = (f: Fr, start: number, y: number, color: string, light: string, key: string) =>
    Array.from({ length: f.n }, (_, i) => (
      <rect
        key={`${key}${i}`}
        x={x(start + i / f.d)}
        y={y}
        width={unitW / f.d}
        height={rowH}
        fill={light}
        stroke={color}
        strokeWidth={1.5}
      />
    ));
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`${frWords(a)} ${op === '+' ? 'plus' : 'moins'} ${frWords(b)} égale ${frWords(result)}, en ${partName(D)}.`}
    >
      <defs>
        <pattern
          id={hatch}
          width={6}
          height={6}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1={0} y1={0} x2={0} y2={6} stroke={C2} strokeWidth={2} />
        </pattern>
      </defs>
      {rows.map(({ f, y, color, light }, k) => (
        <g key={k}>
          <text
            x={X0 - 6}
            y={y + rowH / 2 + 5}
            textAnchor="end"
            fontSize={14}
            fontWeight={700}
            fill={INK}
          >
            {frPlain(f)}
          </text>
          {cells(f, 0, y, color, light, `r${k}`)}
        </g>
      ))}
      <text
        x={X0 - 6}
        y={yRes + rowH / 2 + 5}
        textAnchor="end"
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        =
      </text>
      {op === '+' ? (
        <>
          {cells(a, 0, yRes, C1, C1_LIGHT, 'sa')}
          {cells(b, va, yRes, C2, C2_LIGHT, 'sb')}
        </>
      ) : (
        <>
          {cells(a, 0, yRes, C1, C1_LIGHT, 'da')}
          <rect
            x={x(va - vb)}
            y={yRes}
            width={vb * unitW}
            height={rowH}
            fill={`url(#${hatch})`}
            stroke={C2}
            strokeWidth={1.5}
          />
        </>
      )}
      {/* les parts communes, en pointillé sur le résultat */}
      {D <= 36 &&
        Array.from({ length: Math.round((op === '+' ? va + vb : va) * D) + 1 }, (_, i) => (
          <line
            key={`t${i}`}
            x1={x(i / D)}
            x2={x(i / D)}
            y1={yRes}
            y2={yRes + rowH}
            stroke={MUTED}
            strokeWidth={0.8}
            strokeDasharray="2 2"
          />
        ))}
      <line x1={X0} x2={x(units)} y1={axisY} y2={axisY} stroke={MUTED} strokeWidth={1.5} />
      {Array.from({ length: units + 1 }, (_, u) => (
        <g key={u}>
          <line x1={x(u)} x2={x(u)} y1={axisY - 5} y2={axisY + 5} stroke={INK} strokeWidth={1.5} />
          <text x={x(u)} y={axisY + 18} textAnchor="middle" fontSize={12} fill={INK}>
            {u}
          </text>
        </g>
      ))}
      <line
        x1={x(result.n / result.d)}
        x2={x(result.n / result.d)}
        y1={yRes - 4}
        y2={axisY}
        stroke={INK}
        strokeWidth={2}
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Produit                                                             */
/* ------------------------------------------------------------------ */

function MulMode({ onSend, sendCost }: ToolProps) {
  const [a, setA] = useState<Fr>({ n: 2, d: 3 });
  const [b, setB] = useState<Fr>({ n: 3, d: 4 });
  const r = mulSteps(a, b);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <FractionPicker
          name="Fraction 1"
          color={C1}
          value={a}
          minDen={1}
          maxDen={10}
          onChange={setA}
        />
        <FractionPicker
          name="Fraction 2"
          color={C2}
          value={b}
          minDen={1}
          maxDen={10}
          onChange={setB}
        />
      </div>
      <AreaFigure a={a} b={b} />
      <p className="text-ink-700 text-sm" data-testid="mul-read">
        Les <MathText>{`$${frTex(b)}$`}</MathText> de <MathText>{`$${frTex(a)}$`}</MathText> :{' '}
        {a.n * b.n} case{a.n * b.n > 1 ? 's' : ''} sur {a.d * b.d}.
      </p>
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3">
        <StepList steps={r.steps} testId="mul-steps" />
        <SendResult result={r.result} onSend={onSend} sendCost={sendCost} />
      </section>
    </>
  );
}

function AreaFigure({ a, b }: { a: Fr; b: Fr }) {
  const S = 220;
  const x0 = (VB_W - S) / 2 + 14;
  const y0 = 30;
  const H = y0 + S + 12;
  const cw = S / a.d;
  const rh = S / b.d;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Carré unité : ${frWords(a)} en colonnes bleues, ${frWords(b)} en lignes orange ; ${a.n * b.n} cases sur ${a.d * b.d} sont dans les deux.`}
    >
      <rect x={x0} y={y0} width={(a.n / a.d) * S} height={S} fill={C1} opacity={0.3} />
      <rect x={x0} y={y0} width={S} height={(b.n / b.d) * S} fill={C2} opacity={0.3} />
      {/* les cases prises deux fois */}
      <rect
        x={x0}
        y={y0}
        width={(a.n / a.d) * S}
        height={(b.n / b.d) * S}
        fill="none"
        stroke={INK}
        strokeWidth={3}
      />
      {Array.from({ length: a.d + 1 }, (_, i) => (
        <line
          key={`c${i}`}
          x1={x0 + i * cw}
          x2={x0 + i * cw}
          y1={y0}
          y2={y0 + S}
          stroke={LINE}
          strokeWidth={1}
        />
      ))}
      {Array.from({ length: b.d + 1 }, (_, i) => (
        <line
          key={`r${i}`}
          x1={x0}
          x2={x0 + S}
          y1={y0 + i * rh}
          y2={y0 + i * rh}
          stroke={LINE}
          strokeWidth={1}
        />
      ))}
      <rect x={x0} y={y0} width={S} height={S} fill="none" stroke={INK} strokeWidth={1.5} />
      <text
        x={x0 + ((a.n / a.d) * S) / 2}
        y={y0 - 10}
        textAnchor="middle"
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        {frPlain(a)}
      </text>
      <text
        x={x0 - 8}
        y={y0 + ((b.n / b.d) * S) / 2 + 5}
        textAnchor="end"
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        {frPlain(b)}
      </text>
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Division                                                            */
/* ------------------------------------------------------------------ */

function DivMode({ onSend, sendCost }: ToolProps) {
  const [a, setA] = useState<Fr>({ n: 3, d: 2 });
  const [b, setB] = useState<Fr>({ n: 1, d: 4 });
  const r = divSteps(a, b);
  const { whole, rest } = piecesOf(a, b);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <FractionPicker
          name="Dividende"
          color={C1}
          value={a}
          maxNum={(d) => 3 * d}
          onChange={setA}
        />
        <FractionPicker name="Diviseur" color={C2} value={b} minNum={1} onChange={setB} />
      </div>
      <MeasureFigure a={a} b={b} whole={whole} rest={rest} />
      <p className="text-ink-700 text-sm" data-testid="div-read">
        <MathText>{frenchSpaces(fitSentence(a, b))}</MathText>
      </p>
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3">
        <StepList steps={r.steps} testId="div-steps" />
        <SendResult result={r.result} onSend={onSend} sendCost={sendCost} />
      </section>
    </>
  );
}

function MeasureFigure({ a, b, whole, rest }: { a: Fr; b: Fr; whole: number; rest: Fr }) {
  const hatch = useId().replace(/:/g, '');
  const units = Math.max(1, Math.ceil(a.n / a.d));
  // marge à droite : l'étiquette de la dernière graduation reste visible
  const unitW = (BAR_W - 10) / units;
  const x = (v: number) => X0 + v * unitW;
  const rowH = 24;
  const yA = 8;
  const yB = yA + rowH + 10;
  const axisY = yB + rowH + 14;
  const H = axisY + 22;
  const vb = b.n / b.d;
  const pieceW = vb * unitW;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`${frWords(b)} tient ${whole} fois dans ${frWords(a)}${rest.n ? `, plus ${frWords(rest)} de part` : ''}.`}
    >
      <defs>
        <pattern
          id={hatch}
          width={6}
          height={6}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1={0} y1={0} x2={0} y2={6} stroke={C2} strokeWidth={2} />
        </pattern>
      </defs>
      <text
        x={X0 - 6}
        y={yA + rowH / 2 + 5}
        textAnchor="end"
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        {frPlain(a)}
      </text>
      <rect
        x={X0}
        y={yA}
        width={(a.n / a.d) * unitW}
        height={rowH}
        fill={C1_LIGHT}
        stroke={C1}
        strokeWidth={1.5}
      />
      <text
        x={X0 - 6}
        y={yB + rowH / 2 + 5}
        textAnchor="end"
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        {frPlain(b)}
      </text>
      {Array.from({ length: whole }, (_, i) => (
        <g key={i}>
          <rect
            x={X0 + i * pieceW}
            y={yB}
            width={pieceW}
            height={rowH}
            fill={C2_LIGHT}
            stroke={C2}
            strokeWidth={1.5}
          />
          {pieceW >= 16 && (
            <text
              x={X0 + (i + 0.5) * pieceW}
              y={yB + rowH / 2 + 4}
              textAnchor="middle"
              fontSize={12}
              fill={INK}
            >
              {i + 1}
            </text>
          )}
        </g>
      ))}
      {rest.n > 0 && (
        <rect
          x={X0 + whole * pieceW}
          y={yB}
          width={(rest.n / rest.d) * pieceW}
          height={rowH}
          fill={`url(#${hatch})`}
          stroke={C2}
          strokeWidth={1.5}
          strokeDasharray="3 2"
        />
      )}
      <line x1={X0} x2={x(units)} y1={axisY} y2={axisY} stroke={MUTED} strokeWidth={1.5} />
      {Array.from({ length: units + 1 }, (_, u) => (
        <g key={u}>
          <line x1={x(u)} x2={x(u)} y1={axisY - 5} y2={axisY + 5} stroke={INK} strokeWidth={1.5} />
          <text x={x(u)} y={axisY + 18} textAnchor="middle" fontSize={12} fill={INK}>
            {u}
          </text>
        </g>
      ))}
    </svg>
  );
}
